import type { SvgIconComponent } from "@mui/icons-material";
import HikingIcon from "@mui/icons-material/Hiking";
import StoreIcon from "@mui/icons-material/Store";
import {
  Alert,
  Card,
  CardActionArea,
  CardContent,
  CircularProgress,
  Stack,
  Typography,
} from "@mui/material";
import { useGetContextsQuery } from "Auth/Api/Queries/Context/useGetContextsQuery";
import { AddHunterRoleForm } from "Components/Roles/AddHunterRoleForm";
import { AddMerchantRoleForm } from "Components/Roles/AddMerchantRoleForm";
import { ButtonLink } from "Components/Shared/ButtonLink";
import { useIsUserInMooneLite } from "Hooks/useIsUserInMooneLite";
import { AccessRoleCodes, isBackOfficeRole } from "Models/AccessRoleCodes";
import { useState } from "react";
import { useLocation } from "react-router-dom";
import { Resources, useResource } from "Translations/Resources";
import { appUrl } from "Utils/UrlUtils";

const COMPONENT_RESOURCES = Resources.Roles.AddRole;

type SelectableRole =
  | typeof AccessRoleCodes.Hunter
  | typeof AccessRoleCodes.Merchant;

type RoleCardProps = {
  Icon: SvgIconComponent;
  title: string;
  description: string;
  isDisabled: boolean;
  isSelected: boolean;
  onSelect: () => void;
};

const RoleCard = ({
  Icon,
  title,
  description,
  isDisabled,
  isSelected,
  onSelect,
}: RoleCardProps) => (
  <Card
    variant="outlined"
    sx={isSelected ? { borderColor: "primary.main" } : undefined}
  >
    <CardActionArea onClick={onSelect} disabled={isDisabled}>
      <CardContent>
        <Stack direction="row" gap={2} alignItems="center">
          <Icon color={isDisabled ? "disabled" : "primary"} />
          <Stack>
            <Typography
              variant="subtitle1"
              color={isDisabled ? "text.disabled" : undefined}
            >
              {title}
            </Typography>
            <Typography
              variant="body2"
              color={isDisabled ? "text.disabled" : "text.secondary"}
            >
              {description}
            </Typography>
          </Stack>
        </Stack>
      </CardContent>
    </CardActionArea>
  </Card>
);

export const AddRolePageContent = () => {
  const { t } = useResource();
  const { isLiteTier, isLiteBecauseOfBank } = useIsUserInMooneLite();
  const { data, isLoading } = useGetContextsQuery();
  const location = useLocation();
  // The intent banner passes the role the person already chose — the picker stays visible so
  // they can still change their mind, but their choice starts selected.
  const preselectedRole = (
    location.state as { preselectedRole?: string } | null
  )?.preselectedRole;
  const [selectedRole, setSelectedRole] = useState<SelectableRole | null>(
    preselectedRole === AccessRoleCodes.Merchant ||
      preselectedRole === AccessRoleCodes.Hunter
      ? preselectedRole
      : null,
  );

  if (isLoading) {
    return (
      <Stack alignItems="center" py={4}>
        <CircularProgress size={24} />
      </Stack>
    );
  }

  const contexts = data?.contexts;

  // No contexts = a legacy, not-yet-migrated account — role addition is Identity-native only.
  if (!contexts) {
    return (
      <Alert severity="info">{t(COMPONENT_RESOURCES.MigrationRequired)}</Alert>
    );
  }

  // A back-office account holds exactly its one internal role and can never add another — the
  // route is gated too, this covers a direct navigation racing the contexts query.
  const isBackOfficeAccount = contexts.some((context) =>
    isBackOfficeRole(context.accessRoleCode),
  );

  if (isBackOfficeAccount) {
    return (
      <Alert severity="info">{t(COMPONENT_RESOURCES.BackOfficeAccount)}</Alert>
    );
  }

  // Business roles require finished Customer KYC — point Lite users at it instead of the picker.
  if (isLiteTier) {
    return (
      <Stack gap={2}>
        <Alert severity="info">
          {t(
            isLiteBecauseOfBank
              ? COMPONENT_RESOURCES.BankVerificationRequired
              : COMPONENT_RESOURCES.KycRequired,
          )}
        </Alert>
        <ButtonLink
          link={appUrl(
            isLiteBecauseOfBank
              ? "banking-auth"
              : "complete-customer-onboarding",
          )}
          text={t(
            isLiteBecauseOfBank
              ? Resources.Components.MooneLitePopup.ConnectBankAccountButton
              : Resources.Components.MooneLitePopup.CompleteRegistrationButton,
          )}
          variant="contained"
        />
      </Stack>
    );
  }

  const hasHunterRole = contexts.some(
    (context) =>
      context.accessRoleCode === AccessRoleCodes.Hunter ||
      context.accessRoleCode === AccessRoleCodes.HunterPlus,
  );

  return (
    <Stack gap={3}>
      <Typography variant="body1">
        {t(COMPONENT_RESOURCES.PickerTitle)}
      </Typography>
      <Stack gap={2}>
        <RoleCard
          Icon={HikingIcon}
          title={t(COMPONENT_RESOURCES.HunterCard.Title)}
          description={
            hasHunterRole
              ? t(COMPONENT_RESOURCES.HunterCard.AlreadyHeld)
              : t(COMPONENT_RESOURCES.HunterCard.Description)
          }
          isDisabled={hasHunterRole}
          isSelected={selectedRole === AccessRoleCodes.Hunter}
          onSelect={() => setSelectedRole(AccessRoleCodes.Hunter)}
        />
        <RoleCard
          Icon={StoreIcon}
          title={t(COMPONENT_RESOURCES.MerchantCard.Title)}
          description={t(COMPONENT_RESOURCES.MerchantCard.Description)}
          isDisabled={false}
          isSelected={selectedRole === AccessRoleCodes.Merchant}
          onSelect={() => setSelectedRole(AccessRoleCodes.Merchant)}
        />
      </Stack>
      {selectedRole === AccessRoleCodes.Hunter && !hasHunterRole && (
        <AddHunterRoleForm />
      )}
      {selectedRole === AccessRoleCodes.Merchant && <AddMerchantRoleForm />}
    </Stack>
  );
};
